import { lazy, Suspense, useState } from "react";
import { Link } from "react-router";
import { formatCurrency, formatDateLocale } from "@/shared/utils/formatter.utils";
import { accountingService } from "../services/accounting.service";
import type { BankMovement, LedgerAllocation } from "../types/accounting.types";

const MovementAllocationsDrawer = lazy(() => import("./MovementAllocationsDrawer"));

/** A read-only annotation; never becomes the editable accounting label. */
export const SepaAllocationReturnNotice = ({ allocation }: { allocation: LedgerAllocation }) => {
  const [movement, setMovement] = useState<BankMovement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const openCredit = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const credit = await accountingService.getBankMovement(id);
      if (credit) setMovement(credit);
      else setError("Le crédit de retour est introuvable.");
    }
    catch {
      setError("Impossible de charger le crédit de retour. Réessayez.");
    }
    finally {
      setLoading(false);
    }
  };
  if (!allocation.sepaReturns?.length) return null;
  return (
    <div className="space-y-1 px-2 pb-1 text-[11px] leading-normal text-slate-600" onClick={(event) => event.stopPropagation()}>
      {allocation.sepaReturns.map((entry) => (
        <div key={`${entry.date}-${entry.returnMovementId ?? "unavailable"}`}>
          <p>{`Rejet bancaire — ${formatCurrency(entry.amount)} recrédités le ${formatDateLocale(entry.date)}`}</p>
          <div className="flex flex-wrap gap-x-3">
            {entry.returnMovementId && (
              <button type="button" className="underline underline-offset-2" disabled={loading} onClick={() => void openCredit(entry.returnMovementId!)} aria-label="Voir le crédit de retour">Voir le crédit</button>
            )}
            {entry.retryRemittanceIds.map((id) => (
              <Link key={id} className="underline underline-offset-2" to={`/accounting/ledger/sepa-remittances?remittance=${encodeURIComponent(id)}`}>Voir la remise de relance</Link>
            ))}
          </div>
        </div>
      ))}
      {loading && <p role="status">Chargement du crédit…</p>}
      {error && <p role="alert">{error}</p>}
      {movement && (
        <Suspense fallback={<p role="status">Chargement du mouvement…</p>}>
          <MovementAllocationsDrawer
            key={movement.id}
            open
            bankMovement={movement}
            width="min(893px, 100vw)"
            onOpenChange={(open) => { if (!open) setMovement(null); }}
            onBack={() => setMovement(null)}
            onBankMovementUpdated={setMovement}
            onBankMovementDeleted={() => setMovement(null)}
          />
        </Suspense>
      )}
    </div>
  );
};
